import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faCircleInfo as faCircleInfoRegular } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faCircleInfo as faCircleInfoSolid } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { IconButton } from '../icon-buttons/IconButton';
import { Tooltip } from './Tooltip';

export function InfoTooltip({
  variant = 'filled',
  inverted = false,
  portal = false,
  initialOpen = false,
  size = 18,
  children,
  delayCloseMs,
  ...props
}: {
  variant?: 'filled' | 'outlined';
  inverted?: boolean;
  /** If true, the tooltip will be rendered in a portal */
  portal?: boolean;
  initialOpen?: boolean;
  /**If true, the tooltip content only opens on click */
  size?: number;
  /** must be wrapped in InfoTooltip.Content or InfoTooltip.StyledContent*/
  children?: React.ReactNode;
  /**use when there is clickable content in tooltip */
  delayCloseMs?: number;
} & Omit<React.ComponentProps<typeof Tooltip>, 'setOpenContent' | 'setOpenContentFn'>) {
  return (
    <Tooltip defaultOpen={initialOpen} delayCloseMs={delayCloseMs} {...props}>
      <Tooltip.Trigger
        tabIndex={-1} // Avoid using tab to trigger tooltip
      >
        <IconButton
          className={clsx('focus:[&_svg]:text-black active:[&_svg]:text-black ')}
          variant={inverted ? 'inverted' : 'tertiary'}
          icon={
            <FontAwesomeIcon
              icon={variant === 'outlined' ? faCircleInfoRegular : faCircleInfoSolid}
            />
          }
          size={size}
          iconSize={size}
        />
      </Tooltip.Trigger>
      {portal ? <Tooltip.Portal>{children}</Tooltip.Portal> : children}
    </Tooltip>
  );
}

const StyledTooltipContent = ({
  children,
  ...props
}: { children: React.ReactNode } & React.ComponentProps<typeof Tooltip.StyledContent>) => {
  const { className, ...rest } = props;
  return (
    <Tooltip.StyledContent
      className={clsx('max-w-[240px] px-4 py-6 md:max-w-xs', className)}
      {...rest}
    >
      {children}
    </Tooltip.StyledContent>
  );
};

InfoTooltip.StyledContent = StyledTooltipContent;
InfoTooltip.Content = Tooltip.StyledContent;
